import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle, XCircle, Loader2, Mail } from "lucide-react";
import { toast } from "@/components/ui/sonner";

import AuthLayout from "@/components/auth/AuthLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { handleSubscriptionUpgrade } from "@/lib/subscriptionUtils";

const API_URL = import.meta.env.VITE_API_URL || 'https://localhost:5001/api';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token');
      const subscription = searchParams.get('subscription');
      
      if (!token) {
        setVerificationStatus('error');
        setErrorMessage('No verification token provided');
        return;
      }

      try {
        const response = await fetch(`${API_URL}/auth/verify-email?token=${token}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        const data = await response.json();

        if (data.success) {
          setVerificationStatus('success');
          toast.success("Email verified successfully!");
          
          // Handle subscription upgrade if provided in URL
          if (subscription && ['1', '2', '3', '4', '5'].includes(subscription)) {
            try {
              // Store subscription in localStorage for the upgrade process
              localStorage.setItem('selected-subscription', subscription);
              await handleSubscriptionUpgrade();
              // If handleSubscriptionUpgrade redirects to Stripe, this won't execute
              // If it doesn't redirect (e.g., Free plan), continue to sign in
            } catch (error) {
              console.error('Error handling subscription upgrade:', error);
              // Continue to sign in even if subscription upgrade fails
            }
          }
        } else {
          setVerificationStatus('error');
          setErrorMessage(data.message || 'Verification failed');
        }
      } catch (error) {
        setVerificationStatus('error');
        setErrorMessage('Failed to verify email. Please try again.');
        console.error('Verification error:', error);
      }
    };

    verifyEmail();
  }, [searchParams]);

  const handleSignIn = () => {
    // Preserve subscription parameter when redirecting to signin
    const subscription = searchParams.get('subscription');
    const signinPath = subscription && ['1', '2', '3', '4', '5'].includes(subscription) 
      ? `/signin?subscription=${subscription}` 
      : '/signin';
    navigate(signinPath);
  };

  const handleResendEmail = async () => {
    // This would typically require the user's email address
    // For now, redirect to signin page where they can request a new verification
    // Preserve subscription parameter when redirecting to signin
    const subscription = searchParams.get('subscription');
    const signinPath = subscription && ['1', '2', '3', '4', '5'].includes(subscription) 
      ? `/signin?subscription=${subscription}` 
      : '/signin';
    navigate(signinPath);
  };

  return (
    <AuthLayout
      title="Verify Your Email"
      subtitle="Complete your account setup"
    >
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            {verificationStatus === 'loading' && <Loader2 className="h-6 w-6 animate-spin text-primary" />}
            {verificationStatus === 'success' && <CheckCircle className="h-6 w-6 text-green-600" />}
            {verificationStatus === 'error' && <XCircle className="h-6 w-6 text-red-600" />}
          </div>
          <CardTitle>
            {verificationStatus === 'loading' && 'Verifying your email...'}
            {verificationStatus === 'success' && 'Email verified successfully!'}
            {verificationStatus === 'error' && 'Verification failed'}
          </CardTitle>
          <CardDescription>
            {verificationStatus === 'loading' && 'Please wait while we verify your email address.'}
            {verificationStatus === 'success' && 'Your email has been verified. You can now sign in to your account.'}
            {verificationStatus === 'error' && errorMessage}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {verificationStatus === 'success' && (
            <div className="space-y-3">
              <div className="flex items-center space-x-2 text-sm text-green-600">
                <CheckCircle className="h-4 w-4" />
                <span>Your email address is now verified</span>
              </div>
              <Button onClick={handleSignIn} className="w-full">
                Sign In to Your Account
              </Button>
            </div>
          )}

          {verificationStatus === 'error' && (
            <div className="space-y-3">
              <div className="flex items-center space-x-2 text-sm text-red-600">
                <XCircle className="h-4 w-4" />
                <span>Verification could not be completed</span>
              </div>
              <div className="text-sm text-muted-foreground">
                <p>This could be because:</p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  <li>The verification link has expired</li>
                  <li>The link has already been used</li>
                  <li>The link is invalid</li>
                </ul>
              </div>
              <div className="space-y-2">
                <Button onClick={handleSignIn} className="w-full">
                  Go to Sign In
                </Button>
                <Button onClick={handleResendEmail} variant="outline" className="w-full">
                  <Mail className="mr-2 h-4 w-4" />
                  Request New Verification Email
                </Button>
              </div>
            </div>
          )}

          {verificationStatus === 'loading' && (
            <div className="text-center">
              <div className="flex items-center justify-center space-x-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Verifying your email address...</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </AuthLayout>
  );
};

export default VerifyEmail; 